import filter from 'lodash/filter';
import get from 'lodash/get';
import set from 'lodash/set';
import isFunction from 'lodash/isFunction';
import keyBy from 'lodash/keyBy';
import forOwn from 'lodash/forOwn';
import findIndex from 'lodash/findIndex';
import values from 'lodash/values';
import cloneDeep from 'lodash/cloneDeep';
import isNumber from 'lodash/isNumber';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';
import { http } from '@pendo/http';
import { hasSegmentFlag } from '@/data/flags/local-flags';
import { fireGuideTrackEvents } from '@/guides/track-events/state-change.js';
import { fireGuideCreatedTrackEvent } from '@/guides/track-events/guide-created/guide-created.js';
import {
    loadGuides,
    loadGuide,
    saveGuide,
    saveGuideTranslations,
    deleteGuide,
    saveStep,
    deleteStep,
    saveGuideRedisplay,
    isNpsGuide,
    getClassicDesignerTemplates,
    cloneGuideViaEndpoint,
    setGuideDNT,
} from './guides.utils.js';
import { canEditGuide } from '@/vuex/modules/guides/guide-permissions.utils';
import {
    getContent,
    getGuideStepsContent,
    getIsArchived,
    isInV1Experiment,
    GUIDE_STATE,
    updateClonedGuideActivationLocation,
    removeLaunchMethodFromGuide,
    getGuideSegmentVisitorCount,
    isDoNotTrackGuide,
} from '@/guides/helpers/common.util.js';
import { AppsService as Apps } from '@/common/apps/apps.service';
import {
    SdkTools,
    MinAgentVersion,
    GuideService,
    Automations,
    BuildingBlockPropertiesService,
} from '@pendo/services/BuildingBlocks';

const { getMinSdkVersionForGuide, ROOT_MIN_SDK_VERSION } = SdkTools;
import { addAppToEntityList } from '@pendo/one-ui-shared';
import {
    importLocalizationFile,
    clearTranslatedStrings,
} from '@/settings/localization-settings/guide-localization.service.js';
import { connectAggregationToState, withComponent, withStore } from '@pendo/agg-connect';
import { NpsPollResponsesAgg } from '@/net-promoter-system/nps-responses-table/nps-responses.aggregation';
import {
    npsFilters,
    gptSummaryMinResponsesLimit,
    gptSummaryMaxResponsesLimit,
    NPS_GPT_SUMMARY_STATUS,
} from '@/net-promoter-system/constants/nps.constants';
import {
    deleteThemeFromList,
    getAllNpsThemes,
    getGptSummaryText,
    getThemeDisplayName,
    renameThemeFromList,
} from '@/net-promoter-system/nps-responses-table/utils';
import { RECENTLY_SELECTED_HISTORY_TYPES } from '@/vuex/modules/user-settings/history/history.constants';
import { isMobileApp } from '@/utils/apps';
import BuildingBlockGuidesService from '@/guides/services/building-blocks/building-block-guides';

let loadAllPromise;

const guides = {
    namespaced: true,
    state: {
        editing: false,
        cardBeingEdited: null,
        saving: false,
        fetching: false,
        guide: null,
        guideTranslationErrors: {},
        map: {},
        toAddOrUpdate: [],
        hoveredNpsTheme: '',
        npsResponsesAggData: { data: [] },
        allNpsThemes: [],
        sideBladeThemeId: null,
        sideBladeUpdatedResponse: null,
        npsGptSummary: {
            text: '',
            status: '',
        },
        describeYourUseCase: '',
        previewLanguage: '',
        shouldUpdateNpsResponses: false,
        error: null,
        classicDesignerTemplates: [],
    },
    mutations: {
        setEditing (state, { editing, card }) {
            state.editing = editing;
            if (editing && card) {
                state.cardBeingEdited = card;
            } else {
                state.cardBeingEdited = null;
            }
        },
        setSaving (state, { isSaving }) {
            state.saving = isSaving;
        },
        setFetching (state, { isFetching }) {
            state.fetching = isFetching;
        },
        setActiveGuide (state, { guide }) {
            state.guide = guide;
        },
        setActiveGuideById (state, { id }) {
            state.guide = state.map[id];
        },
        setMap (state, { map }) {
            state.map = map;
        },
        setError (state, { error }) {
            state.error = error;
        },
        setGuideTranslationErrors (state, { guideId, brokenLangs }) {
            state.guideTranslationErrors[guideId] = brokenLangs;
        },
        addGuideToMap (state, { guide }) {
            state.map[guide.id] = guide;
        },
        setDescribeYourUseCase (state, { describeYourUseCase }) {
            state.describeYourUseCase = describeYourUseCase;
        },
        updateGuidesMap (state, { guide }) {
            if (guide && !state.map[guide.id]) state.map[guide.id] = {};
            forOwn(guide, (value, key) => {
                state.map[guide.id][key] = value;
            });

            if (get(guide, 'id')) {
                forOwn(state.map[guide.id], (value, key) => {
                    if (!get(guide, key)) delete state.map[guide.id][key];
                });
            }

            delete state.guideTranslationErrors[guide?.id];
        },
        removeGuideFromMap (state, { guide }) {
            if (!guide || !guide.id) return;

            delete state.map[guide.id];
        },
        removeStepFromGuide (state, { step }) {
            const guideSteps = state.map[step.guideId].steps;
            const stepIndex = findIndex(guideSteps, ['id', step.id]);
            if (stepIndex > -1) {
                guideSteps.splice(stepIndex, 1);
            }
        },
        addOrUpdateStepOnGuide (state, { step }) {
            const guideSteps = state.map[step.guideId].steps;
            const stepIndex = findIndex(guideSteps, ['id', step.id]);
            if (stepIndex > -1) {
                guideSteps.splice(stepIndex, 1, step);
            } else {
                guideSteps.push(step);
            }
        },
        sortGuideSteps (state, { guide }) {
            guide.steps = sortBy(guide.steps, ['rank']);
        },
        queueAddOrUpdate (state, { guide }) {
            state.toAddOrUpdate.push(guide);
        },
        clearQueue (state) {
            state.toAddOrUpdate.length = 0;
        },
        setHoveredNpsTheme (state, themeName = '') {
            state.hoveredNpsTheme = themeName;
        },
        setNpsResponsesAggData (state, { aggData }) {
            state.npsResponsesAggData = aggData;
        },
        updateNpsResponseThemes (state, { response, newSelectedThemes }) {
            const responseToUpdtae = state.npsResponsesAggData.data.find(
                (r) => r.browserTime === response.browserTime && r.visitoId === response.VisitorId,
            );

            if (responseToUpdtae) {
                responseToUpdtae.selectedNpsThemes = newSelectedThemes;
            }
        },
        updateNpsResponsesThemesCounts (state, { currentResponse, updatedResponse }) {
            let isThemeInCurrentResponse = false;
            let isThemeInUpdatedResponse = false;
            const responseGroupMap = {
                Promoter: 'promoters',
                Detractor: 'detractors',
                Passive: 'passives',
            };

            const responseGroup = responseGroupMap[currentResponse.responseGroup];

            state.allNpsThemes = state.allNpsThemes.map((themeCounts) => {
                if (themeCounts.name !== npsFilters.noThemeResponses.value) {
                    isThemeInCurrentResponse = currentResponse.selectedNpsThemes.find(
                        (theme) => theme.name === themeCounts.name,
                    );
                    isThemeInUpdatedResponse = updatedResponse.resolvedThemes.selected.find(
                        (theme) => theme.name === themeCounts.name,
                    );
                } else {
                    isThemeInCurrentResponse = !currentResponse.selectedNpsThemes.length;
                    isThemeInUpdatedResponse = !updatedResponse.resolvedThemes.selected.length;
                }

                const currentResponseThemeCounts = isThemeInCurrentResponse ? 1 : 0;
                const updatedResponseThemeCounts = isThemeInUpdatedResponse ? 1 : 0;

                const delta = updatedResponseThemeCounts - currentResponseThemeCounts;

                return {
                    ...themeCounts,
                    [responseGroup]: themeCounts[responseGroup] + delta,
                };
            });
        },
        updateAllNpsThemeList (state, { actionType, theme }) {
            const { displayName, name, lastUpdatedByUser, lastUpdatedAt } = theme;
            switch (actionType) {
                case 'create':
                    state.allNpsThemes.push({
                        name,
                        displayName,
                        passives: 0,
                        promoters: 0,
                        detractors: 0,
                    });
                    break;
                case 'edit':
                    state.allNpsThemes = renameThemeFromList(
                        state.allNpsThemes,
                        name,
                        displayName,
                        lastUpdatedByUser,
                        lastUpdatedAt,
                    );
                    break;
                case 'delete':
                    state.allNpsThemes = deleteThemeFromList(state.allNpsThemes, name);
                    break;
                default:
                    break;
            }
        },
        setNpsThemes (state, themes) {
            state.allNpsThemes = themes;
        },
        setSideBladeThemeId (state, themeId) {
            state.sideBladeThemeId = themeId;
        },
        setSideBladeUpdatedResponse (state, response) {
            state.sideBladeUpdatedResponse = response;
        },
        setNpsGptSummary (state, { status, text }) {
            state.npsGptSummary = { status, text };
        },
        setAllNpsThemesWithCount (state) {
            const aggData = state.npsResponsesAggData.data;

            if (aggData && aggData.length > 1) {
                const responsesThemes = aggData.find((row) => row.counts);
                const allThemesWithCounts = state.allNpsThemes.map((theme) => {
                    const responseTheme = responsesThemes.counts.find((t) => t.themeId === theme.name);
                    const noResponsesTheme = {
                        displayName: theme.displayName,
                        name: theme.name,
                        passives: 0,
                        promoters: 0,
                        detractors: 0,
                    };

                    return responseTheme
                        ? {
                            ...responseTheme,
                            name: responseTheme.themeId,
                            displayName: getThemeDisplayName({
                                displayName: responseTheme?.npsTheme,
                                name: responseTheme.themeId,
                            }),
                            lastUpdatedByUser: theme.lastUpdatedByUser,
                            lastUpdatedAt: theme.lastUpdatedAt,
                        }
                        : noResponsesTheme;
                });
                state.allNpsThemes = allThemesWithCounts;
            }
        },
        setPreviewLanguage (state, { previewLanguage }) {
            state.previewLanguage = previewLanguage;
        },
        setShouldUpdateNpsResponses (state, shouldUpdate) {
            state.shouldUpdateNpsResponses = shouldUpdate;
        },
        setClassicDesignerTemplates (state, classicDesignerTemplates) {
            state.classicDesignerTemplates = classicDesignerTemplates;
        },
    },
    actions: {
        loadAll (
            { commit, dispatch },
            { reFetch = false, expandKey = '*', includeArchived = true, includeMultiApp = true, appId } = {},
        ) {
            if (loadAllPromise && !reFetch) {
                return loadAllPromise;
            }

            dispatch('setFetching', { isFetching: true });

            loadAllPromise = loadGuides({ expandKey, includeArchived, includeMultiApp, appId });

            return loadAllPromise
                .then(({ data: guidesList }) => {
                    commit('setMap', { map: keyBy(guidesList, 'id') });
                    dispatch('setFetching', { isFetching: false });

                    // returning this for backward compatibility - this should be accessed via the list getter
                    return guidesList;
                })
                .catch(() => {
                    loadAllPromise = null;
                    dispatch('setFetching', { isFetching: false });
                });
        },
        async loadClassicDesignerTemplates ({ commit, getters }) {
            const existingClassicDesignerTemplates = getters.classicDesignerTemplates;
            if (existingClassicDesignerTemplates && existingClassicDesignerTemplates.length) {
                return existingClassicDesignerTemplates;
            }

            const { data: classicDesignerTemplates } = await getClassicDesignerTemplates();
            commit('setClassicDesignerTemplates', classicDesignerTemplates);
        },
        async loadOne ({ getters, dispatch, commit }, { setActive = false, reFetch = false, id, tag } = {}) {
            const guideInState = getters.getGuideById(id);
            if ((!tag && !reFetch && !!guideInState) || !id) {
                if (setActive) commit('setActiveGuide', { guide: guideInState });

                return Promise.resolve();
            }

            dispatch('setFetching', { isFetching: true });

            try {
                const { data: guide } = await loadGuide({ id, tag });
                dispatch('setFetching', { isFetching: false });
                dispatch('addOrUpdateMap', { guide });
                if (setActive) {
                    commit('setActiveGuide', { guide });
                }
            } catch {
                dispatch('setFetching', { isFetching: false });
                if (setActive) {
                    commit('setActiveGuide', { guide: null });
                }
            }
        },
        async loadLocalizedGuideContent ({ commit }, { guideId }) {
            const { data: guide } = await loadGuide({ id: guideId });

            // The FE relies on two properties, dom and buildingBlocks on guide steps (especially for
            // resource center). These props are not persisted on the BE and since we are re-fetching
            // the guide here, we call getGuideStepsContent. This function handles creating the dom and
            // buildingBlocks properties on each step and setting their values to the stringified JSON
            // returned from the domUrl and buildingBlocksUrl endpoints specified on the step.
            const translatedStepsWithContent = await getGuideStepsContent(guide.steps);

            set(guide, 'steps', translatedStepsWithContent);

            commit('setActiveGuide', { guide });
            commit('updateGuidesMap', { guide });
        },
        reloadGuides ({ dispatch }) {
            return dispatch('loadAll', { reFetch: true });
        },
        setFetching ({ commit, dispatch }, { isFetching }) {
            commit('setFetching', { isFetching });
            if (!isFetching) {
                dispatch('updateFromQueue');
            }
        },
        setGuideForEditing ({ commit }, { guide }) {
            commit('setActiveGuide', { guide });
        },
        addOrUpdateMap ({ commit, getters, state }, { guide }) {
            if (state.fetching) {
                commit('queueAddOrUpdate', { guide });
            } else if (getters.getGuideById(guide.id)) {
                commit('updateGuidesMap', { guide });
            } else {
                commit('addGuideToMap', { guide });
            }
        },
        updateFromQueue ({ dispatch, state }) {
            state.toAddOrUpdate.forEach((guide) => {
                dispatch('addOrUpdateMap', { guide });
            });
        },
        updateLaunchMethods ({ commit, state }, { launchMethodMap }) {
            for (const [guideId, launchMethod] of Object.entries(launchMethodMap)) {
                if (state.map[guideId]) {
                    commit('updateGuidesMap', {
                        guide: {
                            ...state.map[guideId],
                            launchMethod,
                        },
                    });
                }
            }
        },
        async saveGuide (
            { commit, dispatch, rootGetters },
            {
                guide: guideToSave,
                buildContent = false,
                preserveUpdateInfo = false,
                isOrgResource = false,
                location = '',
            },
        ) {
            if (isFunction(get(guideToSave, 'showsAfter.valueOf'))) {
                guideToSave.showsAfter = guideToSave.showsAfter.valueOf();
            }
            commit('setSaving', { isSaving: true });

            let guideListenPortalIds = [];
            const app = rootGetters['apps/getAppById'](guideToSave.appId);
            if (Apps.isMobile(app)) {
                guideToSave.steps.forEach((step) => {
                    const stepListenPortalIds = GuideService.extractListenPortalIds({ step });
                    step.attributes.launchListenPortal = stepListenPortalIds.length ? stepListenPortalIds : false;

                    guideListenPortalIds = stepListenPortalIds
                        ? guideListenPortalIds.concat(stepListenPortalIds)
                        : guideListenPortalIds;
                });

                if (!guideToSave.minSdkVersion) {
                    try {
                        const steps = guideToSave.steps.map((step) => ({
                            buildingBlocks: typeof step.buildingBlocks === 'string'
                                ? JSON.parse(step.buildingBlocks)
                                : step.buildingBlocks,
                        }));
                        const isAppFlutterCodeless =
                            app.framework === 'flutter' && !rootGetters['apps/isRestrictedMobileApp'](app.id);
                        guideToSave.minSdkVersion =
                            getMinSdkVersionForGuide(
                                { ...guideToSave, steps },
                                ROOT_MIN_SDK_VERSION,
                                app.platform,
                                undefined,
                                { isAppFlutterCodeless },
                            ) || ROOT_MIN_SDK_VERSION;
                    } catch {
                        guideToSave.minSdkVersion = ROOT_MIN_SDK_VERSION;
                    }
                }
            }

            try {
                let { data: guide } = await saveGuide({ guide: guideToSave, preserveUpdateInfo, isOrgResource });
                if (buildContent) {
                    guide = await getContent(guide);
                }
                const { redisplay } = guideToSave;
                if (redisplay) {
                    await saveGuideRedisplay({ guideId: guide.id, redisplay });
                    guide.redisplay = redisplay;
                }

                if (Apps.isMobile(app) && guideListenPortalIds.length) {
                    guideListenPortalIds = uniq(guideListenPortalIds);
                    GuideService.updateGuidesPerPortal(http, {
                        savedPortalIds: [],
                        newPortalIds: guideListenPortalIds,
                        guideId: guide.id,
                    });
                }

                dispatch('addOrUpdateMap', { guide });
                commit('setActiveGuide', { guide });
                commit('setSaving', { isSaving: false });

                fireGuideCreatedTrackEvent(guide, location);

                return guide;
            } catch (error) {
                commit('setSaving', { isSaving: false });

                if (error?.response?.status === 426 && guideToSave?.attributes?.resourceCenter) {
                    throw error;
                }
            }
        },
        async patchGuide ({ state, commit, dispatch }, { guide, remoteGuide, fieldsToPatch, buildContent = false, skipSetActive = false }) {
            commit('setError', { error: null });
            if (guide.state === GUIDE_STATE.deleted && !state.map[guide.id]) return;
            if (isFunction(get(guide, 'showsAfter.valueOf'))) {
                guide.showsAfter = guide.showsAfter.valueOf();
            }
            commit('setSaving', { isSaving: true });

            if (!remoteGuide) {
                remoteGuide = cloneDeep(guide);
            }
            // Patch all fields that can be changed unless some are specified explicitly
            const guideFieldsToPatchDefaults = [
                'audience',
                'audienceUiHint',
                'emailConfiguration',
                'emailState',
                'expiresAfter',
                'name',
                'polls',
                'recurrence',
                'recurrenceEligibilityWindow',
                'showsAfter',
                'state',
                'description',
            ];
            const guideFieldsToPatch = fieldsToPatch || guideFieldsToPatchDefaults;
            try {
                const response = await GuideService.patchGuideFields(http, { guide, remoteGuide, guideFieldsToPatch });
                let updatedGuide;
                /* When the response has no translation states (because they've been deleted)
                    *  this needs to be captured in the updated guide.  The "hacky" implementation
                    *  in the else statement would not override the deleted value.
                    *
                    *  ex:
                    *  Object.assign({state: 1, translationStates:['a']}, {state: 2})
                    *  // returns {state:2, translationStates:['a']}
                    *
                    *  The desired outcome is {state: 2}, hence this branch in logic.
                    */
                if (guide.translationStates && !response.data.translationStates) {
                    updatedGuide = response.data;
                } else {
                    // This is super hacky but we need to keep things like group on the guide.....
                    updatedGuide = Object.assign({}, guide, response.data);
                }

                if (buildContent) {
                    updatedGuide = await getContent(updatedGuide);
                }

                if (!skipSetActive) commit('setActiveGuide', { guide: updatedGuide });
                commit('updateGuidesMap', { guide: updatedGuide });
                commit('setSaving', { isSaving: false });

                dispatch('patchGuideTrackEvents', { updatedGuide, remoteGuide, guide });

                return updatedGuide;
            } catch (err) {
                commit('setError', { error: err });
                commit('setSaving', { isSaving: false });
            }
        },
        async setGuideDNT ({ getters, commit }, { guideId, isDNT }) {
            commit('setError', { error: null });
            commit('setSaving', { isSaving: true });
            try {
                await setGuideDNT(guideId, isDNT);
                const guide = getters.getGuideById(guideId);
                guide.isDNT = isDNT;
                commit('setActiveGuide', { guide });
                commit('updateGuidesMap', { guide });
                commit('setSaving', { isSaving: false });

                return guide;
            } catch (err) {
                commit('setError', { error: err });
                commit('setSaving', { isSaving: false });
            }
        },
        patchGuideAndSteps ({ commit }, { guide, guideFieldsToPatch, stepFieldsToPatch }) {
            const remoteGuide = cloneDeep(guide);
            let newGuide;

            commit('setSaving', { isSaving: true });

            return GuideService.patchGuideFields(http, { guide, remoteGuide, guideFieldsToPatch })
                .then((response) => {
                    newGuide = response.data;

                    return GuideService.patchGuideSteps(http, { guide, remoteGuide: newGuide, stepFieldsToPatch });
                })
                .then((newStepResponses) => {
                    newGuide.steps = newStepResponses.map((response) => response.data);

                    // This is super hacky but we need to keep things like group on the guide...
                    const updatedGuide = Object.assign({}, guide, newGuide);

                    commit('updateGuidesMap', { guide: updatedGuide });
                    commit('setSaving', { isSaving: false });

                    return updatedGuide;
                })
                .catch(() => {
                    commit('setSaving', { isSaving: false });
                });
        },
        patchStepOnGuide ({ commit, getters }, { updatedStep, remoteGuide, stepFieldToPatch }) {
            commit('setSaving', { isSaving: true });

            return GuideService.patchGuideStep(http, { updatedStep, remoteGuide, stepFieldToPatch })
                .then(({ data: step }) => {
                    commit('addOrUpdateStepOnGuide', { step });
                    if (stepFieldToPatch === 'rank') {
                        commit('sortGuideSteps', { guide: remoteGuide });
                    }

                    const updatedGuide = getters.getGuideById(remoteGuide.id);
                    commit('setActiveGuide', { guide: updatedGuide });
                    commit('updateGuidesMap', { guide: updatedGuide });
                    commit('setSaving', { isSaving: false });

                    return updatedGuide;
                })
                .catch(() => {
                    commit('setSaving', { isSaving: false });
                });
        },
        async remove ({ commit, dispatch }, { guide }) {
            dispatch('setFetching', { isFetching: true });
            try {
                await deleteGuide({ id: guide.id });
                commit('removeGuideFromMap', { guide });

                dispatch('setFetching', { isFetching: false });
            } catch (err) {
                dispatch('setFetching', { isFetching: false });
                throw err;
            }
        },
        async saveGuideStep ({ commit, getters }, { remoteGuide, updatedStep }) {
            commit('setSaving', { isSaving: true });

            try {
                const { data: step } = await saveStep({ guide: remoteGuide, step: updatedStep });
                commit('addOrUpdateStepOnGuide', { step });
                commit('setActiveGuide', { guide: getters.getGuideById(remoteGuide.id) });
                commit('setSaving', { isSaving: false });

                return step;
            } catch (error) {
                commit('setSaving', { isSaving: false });

                return error;
            }
        },
        async removeGuideStep ({ commit, getters }, { remoteGuide, step }) {
            commit('setSaving', { isSaving: true });

            try {
                await deleteStep({ guide: remoteGuide, step });
                commit('removeStepFromGuide', { step });
                commit('setActiveGuide', { guide: getters.getGuideById(remoteGuide.id) });
                commit('setSaving', { isSaving: false });
            } catch (error) {
                commit('setSaving', { isSaving: false });

                return error;
            }
        },
        async importGuideLocalization ({ commit, dispatch }, { guideId, langCode, translations }) {
            commit('setSaving', { isSaving: true });

            try {
                await saveGuideTranslations(guideId, langCode, translations);
                await dispatch('loadLocalizedGuideContent', { guideId });
            } finally {
                commit('setSaving', { isSaving: false });
            }
        },
        async importGuideLocalizationFile ({ rootState, commit, dispatch }, { guideId, langCode, file }) {
            // importLocalizationFile bypasses the http interceptor we use to dynamically
            // inject the subId, hence why we need to get it from state and pass it in here
            const subscriptionId = get(rootState, 'subscription.active.id');
            commit('setSaving', { isSaving: true });

            try {
                await importLocalizationFile(subscriptionId, guideId, langCode, file);
                await dispatch('loadLocalizedGuideContent', { guideId });
            } finally {
                commit('setSaving', { isSaving: false });
            }
        },
        async clearGuideTranslationStrings ({ commit, dispatch }, { guideId, langCode }) {
            commit('setSaving', { isSaving: true });

            try {
                await clearTranslatedStrings(guideId, langCode);
                await dispatch('loadLocalizedGuideContent', { guideId });
            } finally {
                commit('setSaving', { isSaving: false });
            }
        },
        async addMinAgentVersion ({ getters, dispatch }, { guideId, minAgentVersion }) {
            const guide = getters.getGuideById(guideId);
            let guideToSave = cloneDeep(guide);
            guideToSave = MinAgentVersion.addMinAgentVersion(guideToSave, minAgentVersion);
            await dispatch('patchGuide', {
                guide: guideToSave,
                remoteGuide: guide,
                fieldsToPatch: ['minAgentVersion', 'attributes'],
            });
        },
        async removeMinAgentVersion ({ getters, dispatch }, { guideId, minAgentVersion }) {
            const guide = getters.getGuideById(guideId);
            let guideToSave = cloneDeep(guide);
            guideToSave = MinAgentVersion.removeMinAgentVersion(guideToSave, minAgentVersion);
            await dispatch('patchGuide', {
                guide: guideToSave,
                remoteGuide: guide,
                fieldsToPatch: ['minAgentVersion', 'attributes'],
            });
        },
        saveNpsResponsesAggData ({ commit }, { status, data = [] }) {
            const aggData = {
                status,
                data,
            };
            commit('setNpsResponsesAggData', { aggData });
            commit('setAllNpsThemesWithCount', { aggData });
        },
        fetchNpsResponses ({ dispatch }, { component, aggConfigField, store }) {
            dispatch('saveNpsResponsesAggData', { status: 'loading' });
            const withComponentParam = { config: aggConfigField };
            const aggConnectors = [withComponent(withComponentParam)];

            // Dashboard widgets pass segment/time/account via aggConfig from widget.definition
            // (including dashboard filter overrides). Do not bind NPS page globalFilters here.
            if (!component.widget) {
                aggConnectors.push(
                    withStore({
                        blacklist: (state, getters) => getters['globalFilters/segmentBlacklist'],
                        timeSeries: (state, getters) => getters['globalFilters/timeSeriesSpec'],
                        segmentId: (state, getters) => getters['globalFilters/segmentId'],
                        includeAnonymous: (state, getters) => getters['globalFilters/segmentIncludeAnonymous'],
                        selectedAccount: (state, getters) => getters['globalFilters/selectedAccountAccount'],
                    }),
                );
            }

            return connectAggregationToState(NpsPollResponsesAgg, ...aggConnectors).bind(component)({
                component,
                store,
            });
        },
        async fetchGuideNpsThemes ({ commit }, { guideId }) {
            try {
                commit('setNpsThemes', await getAllNpsThemes(guideId));
            } catch {
                throw new Error('Failed to retrieve NPS Themes');
            }
        },
        async fetchNpsGptSummary ({ getters, commit }, { theme, ignoreCache }) {
            const { RESOLVED, FAILED, LOADING, INSUFFICIENT_RESPONSES } = NPS_GPT_SUMMARY_STATUS;
            commit('setNpsGptSummary', { status: LOADING });
            setTimeout(async () => {
                try {
                    const aggData = getters.getNpsResponsesAggData.data;
                    if (aggData && aggData.length) {
                        const themeResponses = aggData
                            .filter((row) => row.selectedNpsThemes.some((t) => t.name === theme.name))
                            .slice(0, gptSummaryMaxResponsesLimit);

                        const textualResponses = themeResponses.map((response) => response.followUpResponse);

                        if (textualResponses.length >= gptSummaryMinResponsesLimit) {
                            commit('setNpsGptSummary', {
                                text: await getGptSummaryText({
                                    textualResponses,
                                    theme: theme.displayName,
                                    ignoreCache,
                                }),
                                status: RESOLVED,
                            });
                        } else {
                            commit('setNpsGptSummary', {
                                text: '',
                                status: INSUFFICIENT_RESPONSES,
                            });
                        }
                    }
                } catch {
                    commit('setNpsGptSummary', {
                        text: '',
                        status: FAILED,
                    });
                    throw new Error('Failed to retrieve NPS GPT Summary');
                }
            }, 1200);
        },
        clearNpsGptSummary ({ commit }) {
            commit('setNpsGptSummary', { text: '' });
        },
        async updateGuideName ({ dispatch, getters }, { id, name, skipSetActive = false }) {
            const guideToUpdate = cloneDeep(getters.getGuideById(id));
            guideToUpdate.name = name;

            await dispatch('patchGuide', {
                guide: guideToUpdate,
                fieldsToPatch: ['name'],
                skipSetActive,
            });
        },
        async cloneGuide (
            { commit, dispatch, rootGetters },
            {
                guide,
                app,
                options = {},
            },
        ) {
            if (!guide || !app) throw new Error('Guide and App are required to clone a guide');
            const {
                crossPlatform,
                useDefaultTheme,
                cloneCodeBlock,
                cloneCategory,
                cloneProductArea,
                isCloningIntoJourney = false,
                name = '',
                cloneLocalizationFiles = true,
            } = options;

            const isDifferentAppChosen = guide.appId !== parseInt(app.id);
            let tempGuide = cloneDeep(guide);
            let savedGuide;

            if (!isDifferentAppChosen && !isCloningIntoJourney) {
                // Use server-side clone endpoint for same-app clones; preserves translations
                savedGuide = await cloneGuideViaEndpoint(guide.id, { name, cloneLocalizationFiles });
                if (savedGuide) {
                    dispatch('addOrUpdateMap', { guide: savedGuide });
                }
            } else {
                // When cloning across app or into journeys, don't use server-side clone endpoint

                // Remove journeyId from cloned guide unless cloning into a journey (caller sets journeyId for the target journey)
                if (!isCloningIntoJourney && tempGuide.attributes?.journeyId) {
                    delete tempGuide.attributes.journeyId;
                }
                // Delete audience and set audienceUiHint to empty filters if cloning into journey
                if (isCloningIntoJourney) {
                    if (tempGuide.audience) {
                        delete tempGuide.audience;
                    };
                    tempGuide.audienceUiHint = { filters: [] };
                }
                // Remove automation elementPathRules when featureId is present so the backend can populate it
                if (guide.automations?.length > 0) {
                    tempGuide.automations = Automations.removeElementPathRulesFromFeatureAutomations(tempGuide.automations);
                }
                // Update guide activation location if different app chosen
                if (isDifferentAppChosen) {
                    tempGuide = updateClonedGuideActivationLocation(tempGuide, isMobileApp(app.platform));
                }

                if (isDoNotTrackGuide(tempGuide)) {
                    tempGuide.isDNT = false;
                }

                if (name) tempGuide.name = name;

                // create a copy of the guide
                const clonedGuide = await BuildingBlockGuidesService.copy({
                    guide: { ...tempGuide, appId: parseInt(app.id) },
                    removeTemplateChildren: isDifferentAppChosen,
                });

                // remove RC link if exists by removing the word 'launcher' from launchMethod
                removeLaunchMethodFromGuide(clonedGuide, 'launcher');

                const defaultTheme = rootGetters['themes/getDefaultThemeForAppId'](clonedGuide.appId);

                BuildingBlockPropertiesService.clone({
                    guide: clonedGuide,
                    platform: app.platform,
                    preventCodeBlock: !cloneCodeBlock,
                    crossPlatform,
                    isNpsGuide: isNpsGuide(clonedGuide),
                    ...(isDifferentAppChosen && useDefaultTheme && { theme: defaultTheme }),
                });

                savedGuide = await dispatch('saveGuide', { guide: clonedGuide });
            }

            if (savedGuide) {
                commit('setActiveGuide', { guide: savedGuide });

                if (cloneProductArea) {
                    const productArea = rootGetters['groups/groupByGuideId']({ guideId: tempGuide.id }) || {};
                    if (productArea && productArea.id) {
                        await dispatch('groups/addItemToGroup', {
                            groupId: productArea.id,
                            itemType: 'guide',
                            itemId: savedGuide.id,
                        }, { root: true });
                    }

                    savedGuide.group = Object.assign({}, productArea);
                }

                if (cloneCategory) {
                    const category = rootGetters['guideCategories/guideCategoryByGuideId']({ guideId: tempGuide.id });
                    if (category && category.id) {
                        await dispatch('guideCategories/addGuideToCategory', {
                            categoryId: category.id,
                            guideId: savedGuide.id,
                        }, { root: true });
                    }
                }

                if (isDifferentAppChosen) {
                    const fromApp = rootGetters['apps/getAppById'](tempGuide.appId);
                    const toApp = rootGetters['apps/getAppById'](savedGuide.appId);

                    window.pendo.track('cloneGuideAnotherApp', {
                        fromAppId: tempGuide.appId,
                        toAppId: savedGuide.appId,
                        fromOs: fromApp?.platform,
                        toOs: toApp?.platform,
                    });
                }
            }

            return savedGuide;
        },
        async patchGuideTrackEvents ({ dispatch, rootGetters }, { updatedGuide, remoteGuide, guide }) {
            const needsVisitorCount = updatedGuide?.state === 'public' && updatedGuide?.state !== remoteGuide?.state;
            let numOfVisitorsInSegment;
            try {
                if (needsVisitorCount) {
                    const aggParams = await dispatch('segments/getGuideSegmentVisitorCountAggParams', { guide }, { root: true });
                    const { targeted, targetedInSegment } = await getGuideSegmentVisitorCount({ guide: updatedGuide, aggParams });
                    numOfVisitorsInSegment = isNumber(targeted) ? targeted : targetedInSegment;
                }
                const app = rootGetters['apps/getAppById'](updatedGuide.appId);
                const guideCategoryName = rootGetters['guideCategories/guideCategoryByGuideId']({
                    guideId: updatedGuide.id,
                })?.name;
                fireGuideTrackEvents(updatedGuide, remoteGuide, numOfVisitorsInSegment, app, guideCategoryName);
            } catch (error) {
                console.error('Error firing track event', error);
            }
        },
    },
    getters: {
        list: (state) =>
            (hasSegmentFlag('archiveGuides')
                ? values(state.map).filter((guide) => !getIsArchived(guide))
                : values(state.map)),
        allGuidesUserCanEdit (state, getters, rootState, rootGetters) {
            const apps = rootGetters['apps/appMapForActiveSubscription'];
            const guides = getters.list;
            const permissibleGuides = guides.filter((guide) => {
                return canEditGuide({
                    guide,
                    target: 'ANY',
                    source: 'ANY',
                });
            });

            return addAppToEntityList(permissibleGuides, apps);
        },
        getGuideById: (state) => (id) => state.map[id],
        isSaving: (state) => state.saving,
        guidesByAppId: (state, getters) => (appId) => getters.list.filter((guide) => guide.appId === appId),
        maxPriority: (state, getters) => {
            const guideList = getters.list;
            const maxPriorityArr = guideList.map((guide) => {
                if (guide.attributes && isNumber(guide.attributes.priority)) {
                    return guide.attributes.priority;
                }

                return -Infinity;
            });

            return Math.max(Math.max(...maxPriorityArr), guideList.length - 1);
        },
        getNonResourceCenterGuides: (state, getters) =>
            filter(getters.list, (guide) => !get(guide, 'attributes.resourceCenter')),
        hasMobileMultiActivations: () => (guide) => {
            return !!guide && !!guide.steps[0].activations;
        },
        calcGuideMinSdk: (state, getters, rootState, rootGetters) => ({ ignoreExperimentMinSdk = false }) => {
            const app = rootGetters['apps/getAppById'](state.guide.appId);
            const steps = state.guide.steps.map(({ buildingBlocks }) => ({
                buildingBlocks: JSON.parse(buildingBlocks),
            }));

            const isAppFlutterCodeless =
                app.framework === 'flutter' && !rootGetters['apps/isRestrictedMobileApp'](app.id);

            return getMinSdkVersionForGuide(
                {
                    ...state.guide,
                    steps,
                },
                undefined,
                app.platform,
                ignoreExperimentMinSdk,
                { isAppFlutterCodeless },
            );
        },
        getHoveredNpsTheme: (state) => state.hoveredNpsTheme,
        getNpsResponsesAggData: (state) => state.npsResponsesAggData,
        fullNpsThemesWithCounts: (state) => {
            let themesArr = [];
            const aggData = state.npsResponsesAggData.data;

            if (aggData && aggData.length) {
                const countsRow = aggData.find((row) => row.counts);

                if (countsRow) {
                    themesArr = countsRow.counts;
                }
            }

            return themesArr.filter((theme) => theme.npsTheme);
        },
        npsThemesWithCounts: (state, { fullNpsThemesWithCounts }) => {
            return fullNpsThemesWithCounts.filter((t) => t.npsTheme !== npsFilters.noThemeResponses.value);
        },
        getPreviewLanguage: (state) => state.previewLanguage,
        isEmail: (state) => !!get(state, 'guide.attributes.isEmail', false),
        getNpsGuides: (state) => values(state.map).filter((guide) => isNpsGuide(guide)),
        getPollGuides: (state) => values(state.map).filter((guide) => guide.polls?.length > 0 && !isNpsGuide(guide)),
        analyticsGuidesListWithStars: (state, getters, _rootState, rootGetters) => {
            const isStarred = rootGetters['stars/getStarByItemId'];

            return getters.list.map((guide) =>
                Object.freeze({
                    id: guide.id,
                    name: guide.name,
                    state: guide.state,
                    lastUpdatedAt: guide.lastUpdatedAt,
                    appId: guide.appId,
                    appIcon: rootGetters['apps/getIconByAppId'](guide.appId),
                    kind: guide.kind,
                    isStarred: !!isStarred(guide.id),
                    icon: guide.icon,
                }),
            );
        },
        recentGuidesInReporting: (state, _getters, _rootState, rootGetters) => {
            const isStarred = rootGetters['stars/getStarByItemId'];

            return rootGetters['userSettings/history/itemHistory'](
                RECENTLY_SELECTED_HISTORY_TYPES.GUIDE_RECENTLY_SELECTED,
            )
                .filter(({ id }) => state.map[id])
                .map(({ id, timestamp }) => {
                    const guide = state.map[id];
                    const app = rootGetters['apps/getAppById'](guide.appId);

                    return {
                        id: guide.id,
                        name: guide.name,
                        state: guide.state,
                        lastUpdatedAt: guide.lastUpdatedAt,
                        appId: guide.appId,
                        appIcon: rootGetters['apps/getIconByAppId'](guide.appId),
                        appDisplayName: app ? app.displayName : undefined,
                        kind: guide.kind,
                        isStarred: !!isStarred(guide.id),
                        icon: guide.icon,
                        lastUsed: timestamp,
                    };
                });
        },
        isV1ExperimentsEnabled: (state, getters, rootState, rootGetters) => {
            if (!rootGetters['productLines/hasProductLinePurchased']('guides', 'experimentsEnabled')) {
                return false;
            }

            return getters.list.some((guide) => isInV1Experiment(guide));
        },
        isGuideEffectivenessEnabled: (state, getters, rootState, rootGetters) => {
            return rootGetters['productLines/hasProductLinePurchased']('guides', 'guideEffectivenessEnabled');
        },
        isLocalizationEnabled: (state, getters, rootState, rootGetters) => (appId) => {
            const app = rootGetters['apps/getAppById'](appId);
            const localizationEnabledForApp = get(app, 'applicationFlags.guideLocalization', false);
            const hasPurchasedGuidesLocalization = rootGetters['productLines/hasProductLinePurchased'](
                'guides',
                'localizationEnabled',
            );

            return localizationEnabledForApp && hasPurchasedGuidesLocalization;
        },
        classicDesignerTemplates: (state) => state.classicDesignerTemplates,
    },
};

export function __resetLoadAllPromiseForUnitTests () {
    loadAllPromise = null;
}

export default guides;
